import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { fetchAuditData } from "redux/actions/viewManualOrdersAuditDetailsAction";
import { format } from "date-fns";
import { LabelWithControl } from "components/common/LabelControl";
import { SOMS_API_KEY } from "services/CONSTANTS";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import HistoryIcon from "@mui/icons-material/History";
import { downloadManualOrdersAudit } from "redux/actions/downloadManualOrdersAuditAction";
import { PAGE } from "components/common/CONSTANTS";
import {
  CLEAR_AUDIT_DATA,
  FETCH_AUDIT_ORDERS_RESET,
  GET_MANUAL_ORDER_AUDIT_RESET_DOWNLOAD,
} from "redux/actions/action-type";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

export const ViewManualOrdersAuditDetails = () => {
  const { orders, metadata, loading, errorDetails } = useSelector(
    (state) => state.orders,
  );
  const { csvAuditData, isDownloadManualOrdersAuditError } = useSelector(
    (state) => state.downloadManualOrdersAuditReducer,
  );

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [orderDateFrom, setOrderDateFrom] = useState(new Date());
  const [orderDateTo, setOrderDateTo] = useState(new Date());
  const [packId, setPackId] = useState("");
  const [itemId, setItemId] = useState("");
  const [shipFromLocationId, setShipFromLocationId] = useState("");
  const [shipToLocationId, setShipToLocationId] = useState("");
  const [isSearched, setIsSearched] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [orderType, setOrderType] = useState("CENTRAL_SUPPLYCHAIN");
  const [changeOrderType, setChangeOrderType] = useState(true);
  const [validationErrors, setValidationErrors] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [approvalStatus, setApprovalStatus] = useState("all");
  const [selectedHistory, setSelectedHistory] = useState({
    history: [],
    packId: "",
    itemId: "",
    itemDescription: "",
    quantityOrdered: "",
  });

  const userType = useSelector((state) => state.user);
  const { locationResponse } = useSelector((state) => state.locations);
  const { accessiblePages } = userType.userType;
  const [validStores, setValidStores] = useState([]);

  const FETCH_ORDER_LIMIT = 100;
  const [offset, setOffset] = useState(0);
  const tableContainerRef = useRef();
  const isMounted = useRef();

  const handleInfiniteScroll = useCallback(async () => {
    try {
      const { scrollTop, scrollHeight, clientHeight } =
        tableContainerRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 1 && !loading) {
        setOffset(orders.length);
      }
    } catch (error) {
      console.log(error, "error");
    }
  }, [tableContainerRef, orders, loading]);

  useEffect(() => {
    if (orders.length > 0) {
      const tableContainer = tableContainerRef.current;
      tableContainer.addEventListener("scroll", handleInfiniteScroll);
      return () =>
        tableContainer.removeEventListener("scroll", handleInfiniteScroll);
    }
  }, [handleInfiniteScroll, orders]);

  useEffect(() => {
    if (metadata.next) {
      callSearchOrdersApi();
    }
  }, [offset]); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (accessiblePages) {
      let channelList =
        accessiblePages[PAGE.VIEW_MANUAL_ORDERS_AUDIT_DETAILS.key];
      const stores = [];
      locationResponse.forEach(function (location) {
        if (channelList.indexOf(location.channel.toLowerCase()) > -1) {
          stores.push(location.locationId);
        }
      });
      setValidStores(stores);
    }
  }, [accessiblePages, locationResponse]);

  useEffect(() => {
    if (isDownloadManualOrdersAuditError) {
      const errors = [];
      errors.push(
        "No matching records found to download! Please check your search criteria.",
      );
      if (errors.length > 0) {
        setValidationErrors(errors);
        return;
      } else {
        setValidationErrors([]); // Clear validation errors if all fields are valid
      }
    }
  }, [isDownloadManualOrdersAuditError]);

  //Disabled eslint check. Adding missing dependency causes rerenders.
  useEffect(() => {
    if (isMounted.current) return;

    isMounted.current = true;
    dispatch({ type: FETCH_AUDIT_ORDERS_RESET });
    dispatch({ type: GET_MANUAL_ORDER_AUDIT_RESET_DOWNLOAD });
    return () => {
      isMounted.current = false;
      dispatch({ type: FETCH_AUDIT_ORDERS_RESET });
      dispatch({ type: GET_MANUAL_ORDER_AUDIT_RESET_DOWNLOAD });
    };
  }, []); //eslint-disable-line react-hooks/exhaustive-deps

  const isNumericWithCommas = (value) => {
    return value.split(",").every((v) => /^[0-9, \s]*$/.test(v.trim()));
  };

  const updateFieldErrors = (field, message) => {
    setFieldErrors((previous) => ({
      ...previous,
      [field]: {
        error: true,
        message: message,
      },
    }));
  };

  const handleSearch = () => {
    const updatedOffset = 0;
    setOffset(updatedOffset);
    dispatch({ type: FETCH_AUDIT_ORDERS_RESET });
    callSearchOrdersApi(updatedOffset);
  };

  const callSearchOrdersApi = (updatedOffset) => {
    const errors = [];
    const validSearchStores = [];
    let hasFieldErrors = false;
    // Validation for both 'Order Date From' and 'Order Date To'
    if (!orderDateFrom || !orderDateTo) {
      errors.push(
        "Please enter both 'Order Date From' & 'Order Date To' dates",
      );
    }

    // Prepare arrays for packId and itemId (handling case where input is empty)
    const packIdArray = packId
      ? packId.split(",").map((num) => Number(num.trim()))
      : [];
    const itemIdArray = itemId
      ? itemId.split(",").map((num) => Number(num.trim()))
      : [];
    const shipFromLocationIdArray = shipFromLocationId
      ? shipFromLocationId.split(",").map((num) => Number(num.trim()))
      : [];

    setFieldErrors({});
    if (packId && !isNumericWithCommas(packId)) {
      updateFieldErrors("packId", "PIN should contain only numeric values.");
      hasFieldErrors = true;
    }
    if (packIdArray.length > 50) {
      updateFieldErrors("packId", "PIN can have a maximum of 50 entries.");
      hasFieldErrors = true;
    }
    if (itemId && !isNumericWithCommas(itemId)) {
      updateFieldErrors("itemId", "MIN should contain only numeric values.");
      hasFieldErrors = true;
    }
    if (itemIdArray.length > 50) {
      updateFieldErrors("itemId", "MIN can have a maximum of 50 entries.");
      hasFieldErrors = true;
    }
    if (shipFromLocationId && !isNumericWithCommas(shipFromLocationId)) {
      updateFieldErrors(
        "shipFromLocationId",
        "Depot should contain only numeric values.",
      );
      hasFieldErrors = true;
    }
    if (shipFromLocationIdArray.length > 50) {
      updateFieldErrors(
        "shipFromLocationId",
        "Depot can have a maximum of 50 entries.",
      );
      hasFieldErrors = true;
    }
    if (shipToLocationId && !isNumericWithCommas(shipToLocationId)) {
      updateFieldErrors(
        "shipToLocationId",
        "Store should contain only numeric values.",
      );
      hasFieldErrors = true;
    }
    if (orderDateFrom && !dayjs(orderDateFrom).isValid()) {
      updateFieldErrors("orderDateFrom", "Please enter date in correct format");
      hasFieldErrors = true;
    }
    if (orderDateTo && !dayjs(orderDateTo).isValid()) {
      updateFieldErrors("orderDateTo", "Please enter date in correct format");
      hasFieldErrors = true;
    }

    // If errors, set validation errors and return
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    } else {
      if (shipToLocationId) {
        const storeArr = shipToLocationId
          .split(",")
          .map((x) => parseInt(x.trim()));
        const invalidStores = [];
        storeArr.forEach(function (store) {
          if (validStores.indexOf(store) < 0) {
            invalidStores.push(store);
          } else {
            validSearchStores.push(store);
          }
        });
        if (invalidStores.length > 0) {
          errors.push(
            `The following store(s) are not valid: ${invalidStores.join(", ")}`,
          );
        }
      }
      if (errors.length > 0) {
        setValidationErrors(errors);
      } else {
        setValidationErrors([]);
      }
    }

    if (
      (!shipToLocationId || validSearchStores.length > 0) &&
      !hasFieldErrors
    ) {
      // Prepare the request body (reqBody) with all necessary data
      const reqBody = {
        orderDateFrom: orderDateFrom
          ? format(orderDateFrom, "yyyy-MM-dd")
          : null, // Make sure fromFormattedDate is correctly set
        orderDateTo: orderDateTo ? format(orderDateTo, "yyyy-MM-dd") : null, // Make sure toFormattedDate is correctly set
        packId: packIdArray,
        itemId: itemIdArray,
        orderStatus: approvalStatus === "all" ? "" : approvalStatus, // If you have any status filter, make sure it is set
        isAuditScreen: true,
        shipFromLocationId: shipFromLocationIdArray,
        shipToLocationId: shipToLocationId ? validSearchStores : validStores,
        userType: orderType === "all" ? "" : orderType,
      };

      // Dispatch the action to fetch data, passing the reqBody directly
      dispatch(
        fetchAuditData({
          apiKey: SOMS_API_KEY, // Assuming apiKey is available
          limit: FETCH_ORDER_LIMIT, // Set default limit
          offset: updatedOffset !== undefined ? updatedOffset : offset, // Set default offset
          reqBody: reqBody, // Send the request body as the payload
        }),
      );

      // Set the search state
      setIsSearched(true);
    }
  };

  const downloadCSV = async () => {
    const errors = [];
    const validSearchStores = [];

    // Validation for both 'Order Date From' and 'Order Date To'
    if (!orderDateFrom || !orderDateTo) {
      errors.push(
        "Please enter both 'Order Date From' & 'Order Date To' dates",
      );
    }

    // If errors, set validation errors and return
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    } else {
      if (shipToLocationId) {
        const storeArr = shipToLocationId
          .split(",")
          .map((x) => parseInt(x.trim()));
        const invalidStores = [];
        storeArr.forEach(function (store) {
          if (validStores.indexOf(store) < 0) {
            invalidStores.push(store);
          } else {
            validSearchStores.push(store);
          }
        });
        if (invalidStores.length > 0) {
          errors.push(
            `The following store(s) are not valid: ${invalidStores.join(", ")}`,
          );
        }
      }
      if (errors.length > 0) {
        setValidationErrors(errors);
      } else {
        setValidationErrors([]); // Clear validation errors if all fields are valid
      }
    }
    const packIdArray = packId
      ? packId.split(",").map((num) => Number(num.trim()))
      : [];
    const itemIdArray = itemId
      ? itemId.split(",").map((num) => Number(num.trim()))
      : [];
    const shipFromLocationIdArray = shipFromLocationId
      ? shipFromLocationId.split(",").map((num) => Number(num.trim()))
      : [];

    if (!shipToLocationId || validSearchStores.length > 0) {
      // Prepare the request body (reqBody) with all necessary data
      const reqBody = {
        orderDateFrom: orderDateFrom
          ? format(orderDateFrom, "yyyy-MM-dd")
          : null,
        orderDateTo: orderDateTo ? format(orderDateTo, "yyyy-MM-dd") : null, // Make sure toFormattedDate is correctly set
        packId: packIdArray,
        itemId: itemIdArray,
        isAuditScreen: true,
        orderStatus: approvalStatus === "all" ? "" : approvalStatus, // If you have any status filter, make sure it is set
        shipFromLocationId: shipFromLocationIdArray,
        shipToLocationId: shipToLocationId ? validSearchStores : validStores,
        userType: orderType === "all" ? "" : orderType,
      };
      // Dispatch the action to fetch data, passing the reqBody directly
      dispatch(
        downloadManualOrdersAudit({
          apiKey: SOMS_API_KEY, // Assuming apiKey is available
          limit: 15000,
          offset: 0, // Set default offset
          reqBody: reqBody, // Send the request body as the payload
        }),
      );
    }
  };
  const generateAndDownloadExcel = useCallback(
    (data) => {
      const exportToExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Audit Details");

        const headers = [
          "Order Date",
          "Manual Order Id",
          "MIN",
          "PIN",
          "Description",
          "Pack Size",
          "Depot",
          "Store",
          "Delivery Date",
          "Order Status",
          "Quantity",
          "Created/Amended Date",
          "Created/Amended By",
          "Approved Date",
          "Approved By",
          "Deleted Date",
          "Deleted By",
        ];
        worksheet.addRow(headers);

        data.forEach((row) => {
          const parentRow = [
            row.orderDate,
            row.manualOrderId,
            row.itemId,
            row.packId,
            row.itemDescription,
            row.packSize,
            row.shipFromLocationId,
            row.shipToLocationId,
            row.deliveryDate,
            row.status,
            "",
            "",
            "",
            "",
            "",
            "",
            "",
          ];
          const parentRowNumber = worksheet.addRow(parentRow).number;

          // Non-mutating sort for child rows
          const sortedAuditDetailsData = [...(Array.isArray(row.auditDetails) ? row.auditDetails : [])].sort(
            (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt),
          );

          sortedAuditDetailsData.forEach((auditDetails) => {
            const childRow = [
              "",
              "",
              "",
              "",
              "",
              "",
              "",
              "",
              "",
              "",
              auditDetails.updatedQuantity,
              ["pending", "auto-approved"].includes(auditDetails.status) &&
              auditDetails.updatedQuantity > 0
                ? auditDetails.updatedAt
                : "-",
              ["pending", "auto-approved"].includes(auditDetails.status) &&
              auditDetails.updatedQuantity > 0
                ? auditDetails.updatedBy
                : "-",
              auditDetails.status === "approved" &&
              auditDetails.updatedQuantity > 0
                ? auditDetails.updatedAt
                : "-",
              auditDetails.status === "approved" &&
              auditDetails.updatedQuantity > 0
                ? auditDetails.updatedBy
                : "-",
              auditDetails.status === "deleted" &&
              auditDetails.updatedQuantity === 0
                ? auditDetails.updatedAt
                : "-",
              auditDetails.status === "deleted" &&
              auditDetails.updatedQuantity === 0
                ? auditDetails.updatedBy
                : "-",
            ];
            const childRowNumber = worksheet.addRow(childRow).number;
            worksheet.getRow(childRowNumber).outlineLevel = 1;
          });

          worksheet.getRow(parentRowNumber).outlineLevel = 0;
        });

        worksheet.properties.outlineProperties = { summaryBelow: true };

        const uniqueCode = Array.from(crypto.getRandomValues(new Uint8Array(4))).map(b => b.toString(16).padStart(2, '0')).join('');
        let currentOrderType = orderType === "all" ? "All" : orderType;
        if (currentOrderType === "STORE_USER") {
          currentOrderType = "Store";
        } else if (currentOrderType === "CENTRAL_SUPPLYCHAIN") {
          currentOrderType = "Supply Chain";
        } else {
          currentOrderType =
            currentOrderType.charAt(0).toUpperCase() +
            currentOrderType.slice(1);
        }
        const currentDate = format(new Date(), "yyyy-MM-dd");
        const fileName = `ManualOrdersAudit_${currentOrderType}_${currentDate}_${uniqueCode}.xlsx`;

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), fileName);
      };

      exportToExcel().catch((err) =>
        console.error("Error exporting Excel:", err),
      );
    },
    [orderType],
  );

  useEffect(() => {
    if (csvAuditData && csvAuditData.length > 0) {
      generateAndDownloadExcel(csvAuditData);
      dispatch({ type: CLEAR_AUDIT_DATA });
    }
  }, [csvAuditData, generateAndDownloadExcel, dispatch]);
  const handleClose = () => navigate("/");

  const handleHistoryClick = (order) => {
    const auditList = Array.isArray(order.auditDetails) ? order.auditDetails : [];
    setSelectedHistory({
      history: auditList,
      packId: order.packId,
      itemId: order.itemId,
      shipToLocationId: order.shipToLocationId, // store
      shipFromLocationId: order.shipFromLocationId, // depot
      itemDescription: order.itemDescription,
      quantityOrdered: auditList[0]?.updatedQuantity || "N/A",
      updatedAt: auditList[0]?.updatedAt || "N/A",
      updatedBy: auditList[0]?.updatedBy || "N/A",
    });
    setDialogOpen(true);
  };
  const handleDialogClose = () => {
    setDialogOpen(false);
  };
  console.log("check");
  const disablePastDate = (date) => {
    const day = dayjs().subtract(22, "day");
    return !dayjs(day).isBefore(date);
  };

  const handleOrderDateFromChange = (newDate) => {
    setOrderDateFrom(newDate);
    if (newDate) {
      const maxToDate = dayjs(newDate).add(4, "day").toDate();
      setOrderDateTo(maxToDate);
    }
  };

  // used to disble dates beyond valid range for Order Date To
  const disableOrderDateTo = (date) => {
    const fromDate = dayjs(orderDateFrom);
    const toDate = fromDate.add(4, "day");

    const currentDate = dayjs(date);

    // Disable any dates before "Order Date From" and after 5 days of "Order Date From"
    return (
      currentDate.isBefore(fromDate, "day") ||
      currentDate.isAfter(toDate, "day")
    );
  };

  function isValidDate(date) {
    return date && !isNaN(new Date(date).getTime());
  }
  const auditDetails = selectedHistory.history || [];
  // Extract and preserve the 0th record for selectedHistory display
  const firstAuditRecord = auditDetails[0] || {};

  // Remove the 0th record for the table
  const historyWithoutFirstRecord = auditDetails.slice(1);

  // Check if there are no records left after removing the 0th record
  const noHistory = historyWithoutFirstRecord.length === 0;

  // Sort the remaining records if any
  const filteredHistory = [...historyWithoutFirstRecord].sort((a, b) => {
    // Prioritize "DELETED" status
    if (a.status === "DELETED" && b.status !== "DELETED") {
      return -1; // a should come first
    }
    if (a.status !== "DELETED" && b.status === "DELETED") {
      return 1; // b should come first
    }

    // Sort by updatedAt in descending order
    const dateA = new Date(a.updatedAt);
    const dateB = new Date(b.updatedAt);
    return dateB - dateA; // Newest first
  });

  const toPascalCaseWithSpaces = (str) => {
    if (!str) return "";
    return str
      .replace(/\s+/g, " ")
      .replace(/-/g, " ")
      .replace(/(?:^\w|\s\w)/g, (match) => match.toUpperCase());
  };

  return (
    <>
      <div className="mainPageStyling">
        <Typography component="h1" variant="h5" sx={{ mb: 2 }}>
          View Manual Orders Audit Details
        </Typography>

        {/* Search Filters */}
        <div className="labelControl">
          <Grid
            container
            spacing={2}
            style={{ marginBottom: "10px", fontSize: "15px" }}
          >
            <Grid item xs={12} sm={6} md={1.5}>
              <LabelWithControl label="Order Date From">
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    value={orderDateFrom}
                    onChange={handleOrderDateFromChange}
                    shouldDisableDate={disablePastDate}
                    format="dd/MM/yyyy"
                    slotProps={{
                      textField: {
                        inputProps: { "data-testid": "order-date-from-input" },
                      },
                    }}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth size="small" />
                    )}
                  />
                </LocalizationProvider>
              </LabelWithControl>
            </Grid>

            <Grid item xs={12} sm={6} md={1.5}>
              <LabelWithControl label="Order Date To">
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    value={orderDateTo}
                    minDate={orderDateFrom}
                    format="dd/MM/yyyy"
                    shouldDisableDate={disableOrderDateTo}
                    onChange={(newDate) => setOrderDateTo(newDate)}
                    slotProps={{
                      textField: {
                        inputProps: { "data-testid": "order-date-to-input" },
                      },
                    }}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth size="small" />
                    )}
                    // disableFuture
                  />
                </LocalizationProvider>
              </LabelWithControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3.5}>
              <LabelWithControl
                label={<>MIN &nbsp;or&nbsp; PIN</>}
              ></LabelWithControl>

              <TextField
                id="min"
                variant="outlined"
                size="small"
                placeholder="MIN"
                value={itemId}
                style={{ width: "47%" }}
                onChange={(e) => {
                  setItemId(e.target.value);
                  if (e.target.value) {
                    setPackId("");
                  }
                }}
                helperText={fieldErrors?.itemId?.message}
                error={fieldErrors?.itemId?.error}
                disabled={packId.length > 0}
                inputProps={{
                  style: {
                    background: packId.length > 0 ? "#f5f5f5" : "inherit",
                  },
                }}
              />
              <TextField
                id="pin"
                variant="outlined"
                size="small"
                placeholder="PIN"
                value={packId}
                style={{ width: "47%", marginLeft: "14px" }}
                onChange={(e) => {
                  setPackId(e.target.value);
                  if (e.target.value) {
                    setItemId("");
                  }
                }}
                helperText={fieldErrors?.packId?.message}
                error={fieldErrors?.packId?.error}
                disabled={itemId.length > 0}
                inputProps={{
                  style: {
                    background: itemId.length > 0 ? "#f5f5f5" : "inherit",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={1.25}>
              <LabelWithControl label="Depot">
                <TextField
                  placeholder="Depot"
                  value={shipFromLocationId}
                  onChange={(e) => setShipFromLocationId(e.target.value)}
                  helperText={fieldErrors?.shipFromLocationId?.message}
                  error={fieldErrors?.shipFromLocationId?.error}
                  fullWidth
                  size="small"
                />
              </LabelWithControl>
            </Grid>

            <Grid item xs={12} sm={6} md={1.25}>
              <LabelWithControl label="Store">
                <TextField
                  placeholder="Store"
                  value={shipToLocationId}
                  onChange={(e) => setShipToLocationId(e.target.value)}
                  helperText={fieldErrors?.shipToLocationId?.message}
                  error={fieldErrors?.shipToLocationId?.error}
                  fullWidth
                  size="small"
                />
              </LabelWithControl>
            </Grid>
            <Grid item xs={12} sm={6} md={1.5}>
              <LabelWithControl label="Select Order Type">
                <Select
                  className="dropDownValue"
                  value={orderType}
                  onChange={(e) => {
                    setOrderType(e.target.value);
                    setChangeOrderType(true);
                    setIsSearched(false);
                  }}
                  // label="Select Order Type"
                  fullWidth
                >
                  <MenuItem value="CENTRAL_SUPPLYCHAIN">Supply Chain</MenuItem>
                  <MenuItem value="STORE_USER">Store</MenuItem>
                  <MenuItem value="all">All</MenuItem>
                </Select>
              </LabelWithControl>
            </Grid>

            <Grid item xs={12} sm={6} md={1.5}>
              <LabelWithControl label="Approval Status">
                <Select
                  className="dropDownValue"
                  value={approvalStatus}
                  onChange={(e) => {
                    setApprovalStatus(e.target.value);
                    // setIsSearched(false)
                  }}
                  fullWidth
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="auto-approved">Auto Approved</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="deleted">Deleted</MenuItem>
                </Select>
              </LabelWithControl>
            </Grid>

            <Grid
              container
              spacing={2}
              style={{
                marginBottom: "4px",
                marginTop: "4px",
                fontSize: "15px",
                justifyContent: "flex-end",
              }}
            >
              <Grid item>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSearch}
                  size="large"
                  style={{
                    fontSize: "12px",
                    fontWeight: "bold",
                    padding: "2px 10px",
                    marginRight: "8px",
                  }}
                >
                  Search
                </Button>

                <Button
                  // href={orders.length > 0 ? "javascript:void(0)" : "javascript:void(0)"}
                  onClick={downloadCSV}
                  variant="contained"
                  size="large"
                  color="primary"
                  style={{
                    fontWeight: "bold",
                    fontSize: "12px",
                    padding: "2px 10px",
                  }}
                >
                  Download
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </div>

        <Grid item>
          {validationErrors.length > 0 && (
            <div className="error-display">
              {validationErrors.map((error, idx) => (
                <p key={idx} style={{ color: "red" }}>
                  {error}
                </p>
              ))}
            </div>
          )}
        </Grid>

        {isSearched && (
          <>
            <TableContainer
              component={Paper}
              ref={tableContainerRef}
              className="tableStructure tableStyle"
            >
              <Table sx={{ borderLeft: "1px solid rgba(224, 224, 224, 1)" }}>
                {orders.length <= 600 && (
                  <TableHead>
                    <TableRow
                      sx={{ backgroundColor: "#004E37", color: "white" }}
                    >
                      {[
                        "",
                        "Order Date",
                        "MIN",
                        "PIN",
                        "Description",
                        "Depot",
                        "Store",
                        "Quantity Ordered",
                        "Order Status",
                        "Created Date",
                        "Created By",
                      ].map((header) => (
                        <TableCell
                          key={header}
                          sx={{
                            fontSize: "12px",
                            fontWeight: "bold",
                            color: "white",
                            padding: "3px",
                            borderRight: "1px solid rgba(224, 224, 224, 1)",
                            textAlign: "center",
                          }}
                        >
                          {header}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                )}
                <TableBody>
                  {orders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={16} style={{ textAlign: "center" }}>
                        {errorDetails && <>{errorDetails.errorMessage}</>}
                        {!errorDetails && (
                          <>
                            No matching records found! Please check your search
                            criteria.
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ) : orders.length > 600 ? (
                    <TableRow>
                      <TableCell
                        colSpan={14}
                        style={{
                          fontSize: "14px",
                          fontWeight: "bold",
                          color: "black",
                          marginBottom: "10px",
                          textAlign: "center",
                        }}
                      >
                        Large volume of data found. Use download button to
                        download manual orders.
                      </TableCell>
                    </TableRow>
                  ) : (
                    orders.map((order, index) => (
                      <TableRow key={index}>
                        <TableCell
                          sx={{
                            fontSize: "12px",
                            padding: "3px",
                            borderRight: "1px solid rgba(224, 224, 224, 1)",
                            width: "20px",
                            textAlign: "center",
                          }}
                        >
                          <IconButton
                            sx={{ padding: "0" }}
                            onClick={() => handleHistoryClick(order)}
                          >
                            <HistoryIcon />
                          </IconButton>
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: "12px",
                            padding: "3px",
                            borderRight: "1px solid rgba(224, 224, 224, 1)",
                            textAlign: "center",
                          }}
                        >
                          {order.orderDate
                            ? format(new Date(order.orderDate), "dd/MM/yyyy")
                            : ""}
                        </TableCell>

                        <TableCell
                          sx={{
                            fontSize: "12px",
                            padding: "3px",
                            borderRight: "1px solid rgba(224, 224, 224, 1)",
                            textAlign: "center",
                          }}
                        >
                          {order.itemId}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: "12px",
                            padding: "3px",
                            borderRight: "1px solid rgba(224, 224, 224, 1)",
                            textAlign: "center",
                          }}
                        >
                          {order.packId}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: "12px",
                            padding: "3px",
                            borderRight: "1px solid rgba(224, 224, 224, 1)",
                            textAlign: "flex-start",
                          }}
                        >
                          <Tooltip title={order.itemDescription} arrow>
                            <span
                              style={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                display: "inline-block",
                                maxWidth: "480px",
                                verticalAlign: "middle",
                              }}
                            >
                              {order.itemDescription}
                            </span>
                          </Tooltip>
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: "12px",
                            padding: "3px",
                            borderRight: "1px solid rgba(224, 224, 224, 1)",
                            textAlign: "center",
                          }}
                        >
                          {order.shipFromLocationId}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: "12px",
                            padding: "3px",
                            borderRight: "1px solid rgba(224, 224, 224, 1)",
                            textAlign: "center",
                          }}
                        >
                          {order.shipToLocationId}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: "12px",
                            padding: "3px",
                            borderRight: "1px solid rgba(224, 224, 224, 1)",
                            width: "60px",
                            textAlign: "center",
                          }}
                        >
                          {order.quantityOrdered}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: "12px",
                            padding: "3px",
                            borderRight: "1px solid rgba(224, 224, 224, 1)",
                            width: "65px",
                          }}
                        >
                          {toPascalCaseWithSpaces(order.status)}
                        </TableCell>
                        {/* it should be oldest updatedAt of auditDetails */}
                        <TableCell
                          sx={{
                            fontSize: "12px",
                            padding: "3px",
                            borderRight: "1px solid rgba(224, 224, 224, 1)",
                            textAlign: "center",
                          }}
                        >
                          {order.updatedAt
                            ? format(new Date(order.updatedAt), "dd/MM/yyyy")
                            : ""}
                        </TableCell>
                        {/* it should be oldest updatedBy of auditDetails */}
                        <TableCell
                          sx={{
                            fontSize: "12px",
                            padding: "3px",
                            borderRight: "1px solid rgba(224, 224, 224, 1)",
                            textAlign: "center",
                          }}
                        >
                          {order.updatedBy}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                  {loading && (
                    <TableRow>
                      <TableCell
                        colSpan={14}
                        style={{
                          textAlign: "center",
                          position: "relative",
                          height: "100px",
                        }}
                      >
                        <div
                          style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            zIndex: 12,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <CircularProgress />
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-start",
                marginTop: 8,
              }}
            ></div>

            {/*Close Button */}
            <Grid
              container
              spacing={2}
              style={{ marginTop: "10px", marginBottom: "10px" }}
              className="paddingTop0 commonButtons marginBottom0"
            >
              <Grid item xs={16} className="innerPadding">
                <Grid container justifyContent="flex-end">
                  <Button
                    variant="contained"
                    color="secondary"
                    size="large"
                    onClick={handleClose}
                    style={{
                      fontSize: "12px",
                      fontWeight: "bold",
                      padding: "2px 10px",
                    }}
                  >
                    Close
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </>
        )}

        {/* Show "Enter your search criteria" when no search has been performed */}
        {(!isSearched || !changeOrderType) && (
          <TableContainer className="tableStructure tableStyle">
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell
                    colSpan={16}
                    style={{ textAlign: "center", borderRight: "0" }}
                  >
                    <div
                      style={{
                        fontWeight: "bold",
                        fontSize: "14px",
                        color: "black",
                        marginBottom: "10px",
                      }}
                    >
                      Please enter your search criteria
                    </div>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Close button at the bottom right when no search is performed */}
        {(!isSearched || !changeOrderType) && (
          <Grid
            container
            justifyContent="flex-end"
            style={{ marginBottom: "10px", marginTop: "10px" }}
            className="paddingTop0 commonButtons marginBottom0"
          >
            <Grid item xs={16} className="innerPadding">
              <Grid container justifyContent="flex-end">
                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  onClick={handleClose}
                  style={{
                    fontSize: "12px",
                    fontWeight: "bold",
                    padding: "2px 10px",
                  }}
                >
                  Close
                </Button>
              </Grid>
            </Grid>
          </Grid>
        )}

        {/* History Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={handleDialogClose}
          PaperProps={{
            style: {
              width: "80%",
              maxWidth: "900px",
            },
          }}
        >
          <DialogTitle>
            <strong>Action History</strong>
          </DialogTitle>
          <DialogContent>
            <div className="dialogDetails">
              <Typography style={{ fontSize: "12px" }}>
                <strong>Original Order Details</strong>
              </Typography>
              <div>
                <Typography style={{ fontSize: "12px" }}>
                  <strong>PIN:</strong> {selectedHistory.packId}
                </Typography>
                <Typography style={{ fontSize: "12px" }}>
                  <strong>MIN:</strong> {selectedHistory.itemId}
                </Typography>
              </div>
              <div>
                <Typography style={{ fontSize: "12px" }}>
                  <strong>Description:</strong>{" "}
                  {selectedHistory.itemDescription}
                </Typography>
              </div>
              <div>
                <Typography style={{ fontSize: "12px" }}>
                  <strong>Store:</strong> {selectedHistory.shipToLocationId}
                </Typography>
                <Typography style={{ fontSize: "12px" }}>
                  <strong>Depot:</strong> {selectedHistory.shipFromLocationId}
                </Typography>
              </div>
              <div>
                <Typography style={{ fontSize: "12px" }}>
                  <strong>Ordered Quantity:</strong>{" "}
                  {firstAuditRecord.updatedQuantity || " "}
                </Typography>
                <Typography style={{ fontSize: "12px" }}>
                  <strong>Created Date:</strong>{" "}
                  {firstAuditRecord.updatedAt
                    ? new Date(firstAuditRecord.updatedAt).toLocaleDateString(
                        "en-GB",
                      ) +
                      " " +
                      new Date(firstAuditRecord.updatedAt).toLocaleTimeString(
                        "en-GB",
                        { hour12: false },
                      )
                    : " "}
                </Typography>
              </div>
              <div>
                <Typography style={{ fontSize: "12px" }}>
                  <strong>Created By:</strong>{" "}
                  {firstAuditRecord.updatedBy || " "}
                </Typography>
              </div>
            </div>

            {/* Action History Table */}
            <TableContainer
              component={Paper}
              className="tableStructure tableStyle tableActionHistory"
            >
              <Table sx={{ borderLeft: "1px solid rgba(224, 224, 224, 1)" }}>
                <TableHead>
                  {/* <TableRow> */}
                  <TableRow sx={{ backgroundColor: "#004E37", color: "white" }}>
                    <TableCell>Changed on</TableCell>
                    <TableCell>Quantity</TableCell>

                    <TableCell>Amended Date</TableCell>
                    <TableCell>Amended By</TableCell>
                    <TableCell>Approved Date</TableCell>
                    <TableCell>Approved By</TableCell>
                    <TableCell>Deleted Date</TableCell>
                    <TableCell>Deleted By</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {noHistory ? (
                    <TableRow>
                      <TableCell
                        colSpan={8} // Adjust the colspan to match the number of columns in your table
                        style={{
                          textAlign: "center",
                          fontSize: "14px",
                          padding: "10px",
                          fontWeight: "bold",
                          color: "#999",
                        }}
                      >
                        No history found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredHistory.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell
                          style={{
                            fontSize: "12px",
                            padding: "3px",
                            borderRight: "1px solid rgba(224, 224, 224, 1)",
                            textAlign: "center",
                          }}
                        >
                          {/* {item.updatedAt} */}
                          {new Date(item.updatedAt).toLocaleDateString(
                            "en-GB",
                          ) +
                            " " +
                            new Date(item.updatedAt).toLocaleTimeString(
                              "en-GB",
                              { hour12: false },
                            )}
                        </TableCell>
                        <TableCell
                          style={{
                            fontSize: "12px",
                            padding: "3px",
                            borderRight: "1px solid rgba(224, 224, 224, 1)",
                            textAlign: "center",
                          }}
                        >
                          {item.updatedQuantity}
                        </TableCell>
                        {/* Amend Column */}
                        <TableCell
                          style={{
                            fontSize: "12px",
                            padding: "3px",
                            borderRight: "1px solid rgba(224, 224, 224, 1)",
                            textAlign: "center",
                          }}
                        >
                          {["pending", "auto-approved"].includes(item.status) &&
                          item.updatedQuantity > 0
                            ? isValidDate(item.updatedAt)
                              ? format(new Date(item.updatedAt), "dd/MM/yyyy")
                              : "-"
                            : "-"}
                        </TableCell>
                        <TableCell
                          style={{
                            fontSize: "12px",
                            padding: "3px",
                            borderRight: "1px solid rgba(224, 224, 224, 1)",
                            textAlign: "center",
                          }}
                        >
                          {["pending", "auto-approved"].includes(item.status) &&
                          item.updatedQuantity > 0
                            ? item.updatedBy
                            : "-"}
                        </TableCell>

                        {/* Approve Column */}
                        <TableCell
                          style={{
                            fontSize: "12px",
                            padding: "3px",
                            borderRight: "1px solid rgba(224, 224, 224, 1)",
                            textAlign: "center",
                          }}
                        >
                          {item.status === "approved" &&
                          item.updatedQuantity > 0
                            ? isValidDate(item.updatedAt)
                              ? format(new Date(item.updatedAt), "dd/MM/yyyy")
                              : "-"
                            : "-"}
                        </TableCell>
                        <TableCell
                          style={{
                            fontSize: "12px",
                            padding: "3px",
                            borderRight: "1px solid rgba(224, 224, 224, 1)",
                            textAlign: "center",
                          }}
                        >
                          {item.status === "approved" &&
                          item.updatedQuantity > 0
                            ? item.updatedBy
                            : "-"}
                        </TableCell>

                        {/* Delete Column */}
                        <TableCell
                          style={{
                            fontSize: "12px",
                            padding: "3px",
                            borderRight: "1px solid rgba(224, 224, 224, 1)",
                            textAlign: "center",
                          }}
                        >
                          {item.status === "deleted" &&
                          item.updatedQuantity === 0
                            ? isValidDate(item.updatedAt)
                              ? format(new Date(item.updatedAt), "dd/MM/yyyy")
                              : "-"
                            : "-"}
                        </TableCell>
                        <TableCell
                          style={{
                            fontSize: "12px",
                            padding: "3px",
                            borderRight: "1px solid rgba(224, 224, 224, 1)",
                            textAlign: "center",
                          }}
                        >
                          {item.status === "deleted" &&
                          item.updatedQuantity === 0
                            ? item.updatedBy
                            : "-"}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <DialogActions>
              <Button
                onClick={handleDialogClose}
                variant="contained"
                color="secondary"
                size="large"
                style={{
                  fontSize: "12px",
                  fontWeight: "bold",
                  padding: "2px 10px",
                }}
              >
                Close
              </Button>
            </DialogActions>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};
